/**
 * WEB-413 — "your session has expired" is not "something went wrong".
 *
 * A standalone web visit has a life: twelve hours, or until the tab closes.
 * When it ends, every screen that fetches anything starts failing at once —
 * the ticket grid, the pick screen, the standings — and each of them reported
 * it with the referee flag and copy like "We could not load your tickets.
 * Please ask an attendant."
 *
 * That is wrong twice over. It tells a player something is broken when nothing
 * is, and it sends them to a member of staff who can do nothing about it. A
 * player who has left a tab open since lunchtime understands "your session has
 * expired" instantly — everybody has met that sentence — and needs exactly one
 * thing next: a way back in.
 *
 * So the message says what happened, and the button restarts the visit.
 *
 * This module is the single place that decides. Each screen asks it rather
 * than growing its own idea of what an expired visit looks like, because the
 * previous arrangement — every catch block inventing its own copy — is how
 * the same event ended up described five different ways, none of them true.
 */
import { readRemoteSession, readWebReturn } from "../context/RemoteLaunchContext";

/**
 * True when the server has told us this visit is over.
 *
 *   410  the launch session aged out
 *   401  the token that carried it expired or was rejected
 *
 * Both mean "start again", and neither is worth explaining to a player.
 *
 * 403 is deliberately absent: that is a real ownership failure, and offering
 * a fresh visit would hide a bug rather than fix one. 400 is absent for the
 * same class of reason — a malformed request is ours, not the player's — with
 * one documented exception in OnsiteGate, where a 400 can only mean the tab
 * lost its session id.
 */
export function isVisitOver(error){
    var status = error && error.response && error.response.status;
    return status === 410 || status === 401;
}

/**
 * Where a restart goes: /go/<slug>, which mints a fresh visit.
 *
 * Three sources, in this order, and the order is the whole point.
 *
 * The remembered slug is ONE localStorage key, overwritten by each /go/ visit.
 * A device that opened two venues holds only the most recent — so restarting a
 * dead Tulalip visit from that key alone could land the player at whichever
 * venue they happened to open last. Rare today, since Tulalip is the only
 * live web venue, but it is a wrong answer waiting for the second one.
 *
 * The session knows its own venue and outlives its own expiry: the blob stays
 * in sessionStorage until something clears it, so an expired visit can still
 * say which venue it belonged to. That is asked first, and the remembered slug
 * is the fallback for when the tab itself is gone.
 *
 * Same precedence SessionEnded.origin() uses, deliberately — two screens
 * answering "which venue was this?" differently is exactly the drift worth
 * avoiding.
 *
 * Must be called BEFORE clearRemoteSession(), or the first two sources are
 * already gone.
 *
 * Null for a kiosk or a partner iframe, where restarting a visit is not a
 * thing that exists.
 */
export function restartPath(){
    var session = readRemoteSession() || {};
    var options = session.displayOptions || {};
    var slug = session.webSlug || options.web_slug
               || (readWebReturn() || {}).slug;
    return slug ? "/go/" + slug : null;
}

/** True if this browser can be offered a restart at all. */
export function canRestart(){
    return restartPath() !== null;
}

/**
 * The modal a screen shows instead of its own error copy.
 *
 * No apology and no diagnosis. The player did nothing wrong, nothing is
 * broken, and nothing they entered is lost — the sentences say so plainly and
 * then get out of the way of the button.
 */
export function visitOverModal(){
    return {
        show: true,
        title: "Your session has expired",
        text: "You have been away a while, so we closed your session.\n"
            + "Nothing is wrong and nothing you picked is lost.\n"
            + "Tap Restart to carry on.",
        restart: restartPath()
    };
}

/**
 * The one line a catch block needs.
 *
 *   .catch(error => {
 *       if (visitOver(error, this, {page_ready: true})) return;
 *       ...its own handling...
 *   })
 *
 * Returns true when it has taken care of the error, so the caller stops.
 *
 * It sets `error_modal` and NOTHING else. Every other piece of state is the
 * caller's, passed in `extra`, because the screens do not agree about what
 * else a failed load means and it is not this function's business to decide.
 * The pick screen deliberately leaves `page_ready` false — its render reaches
 * into a payload that never arrived — while the entry manager and the
 * standings set it true and draw what they already had. Setting it here for
 * everybody would have rendered the pick screen against a null payload.
 */
export function visitOver(error, component, extra){
    if(!isVisitOver(error) || !canRestart()) return false;
    component.setState(Object.assign({ error_modal: visitOverModal() },
                                     extra || {}));
    return true;
}
